import { useState, useEffect } from "react";
import { Search, Library, Loader2, X, ListChecks, Palette, ArrowLeft } from "lucide-react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "./ui/dropdown-menu";
import { PokemonCardTile } from "./PokemonCardTile";
import { IllustratorPicker } from "./IllustratorPicker";
import { AddToListButtons } from "./AddToListButtons";
import { computeVariants } from "../lib/variants";
import { LABELS, VARIANT_ORDER } from "../config/variants";
import { browseCards, fetchFilterOptions } from "../lib/api";

// Attend une pause dans la saisie avant de lancer la recherche réseau,
// pour ne pas interroger le backend à chaque frappe.
const useDebounced = (value, delay = 400) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
};

export const GlobalBrowseView = ({
  collection, conditions = {}, onStatusChange, onRemove, onCardClick,
  lists = [], onCreateList, onAddToList, initialIllustrator = "", initialName = "",
  initialExcludeTcgp = false, backLabel, onBack,
}) => {
  const [nameInput, setNameInput] = useState(initialName);
  // Pré-rempli quand on arrive depuis la recherche globale (clic sur un
  // illustrateur) — sinon vide, comme avant.
  const [illustratorInput, setIllustratorInput] = useState(initialIllustrator);
  // TCG Pocket écarté d'office quand on arrive par un nom de Pokémon (Pokédex,
  // fiche carte) : c'est un jeu à part. Reste débrayable ici.
  const [excludeTcgp, setExcludeTcgp] = useState(initialExcludeTcgp);
  const name = useDebounced(nameInput);
  const illustrator = useDebounced(illustratorInput);
  const [rarity, setRarity] = useState(null);
  const [category, setCategory] = useState(null);

  const [filterOptions, setFilterOptions] = useState({ rarities: [], illustrators: [], categories: [] });
  const [page, setPage] = useState(1);
  const [cards, setCards] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);

  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkVariant, setBulkVariant] = useState("normal");

  useEffect(() => {
    fetchFilterOptions().then(setFilterOptions).catch(err => console.error(err));
  }, []);

  // Un changement de filtre repart de la page 1.
  useEffect(() => { setPage(1); }, [name, rarity, illustrator, category, excludeTcgp]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    browseCards({ name, rarity, illustrator, category, page, excludeTcgp })
      .then(data => {
        if (cancelled) return;
        setCards(prev => (page === 1 ? data.cards : [...prev, ...data.cards]));
        setHasMore(data.has_more);
      })
      .catch(err => console.error("Error browsing cards:", err))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [name, rarity, illustrator, category, page, excludeTcgp]);

  const handleVariantClick = (cardId, variantType, currentStatus, shiftKey) => {
    if (shiftKey) {
      if (currentStatus === "wanted") onRemove(cardId, variantType);
      else onStatusChange(cardId, variantType, "wanted");
    } else {
      if (!currentStatus || currentStatus === "wanted") onStatusChange(cardId, variantType, "owned");
      else onRemove(cardId, variantType);
    }
  };

  const activeFilterCount = [rarity, category].filter(Boolean).length;
  const clearFilters = () => { setRarity(null); setCategory(null); setIllustratorInput(""); };

  const toggleSelectMode = () => {
    setSelectMode(prev => !prev);
    setSelectedIds(new Set());
  };

  const toggleCardSelection = (cardId) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(cardId)) next.delete(cardId);
      else next.add(cardId);
      return next;
    });
  };

  // Seules les cartes sélectionnées qui possèdent réellement le variant
  // choisi sont retenues — même principe que SetDetailView.
  const selectedItems = () => Array.from(selectedIds)
    .map(cardId => cards.find(c => c.id === cardId))
    .filter(card => card && computeVariants(card).some(v => v.type === bulkVariant))
    .map(card => ({ card_id: card.id, variant: bulkVariant }));

  // Voir SetDetailView : on garde la sélection pour pouvoir ajouter
  // plusieurs fois / à plusieurs listes (compteur sur le bouton).
  const addSelectionToList = async (listId) => {
    await onAddToList(listId, selectedItems());
  };

  return (
    <div>
      {/* Arrivée par rebond (une espèce du Pokédex, par exemple) : sans ce
          bouton, il n'y a aucun chemin de retour vers la vue d'origine. */}
      {onBack && (
        <Button variant="ghost" className="mb-3 text-zinc-400 hover:text-white hover:bg-zinc-800"
          onClick={onBack}>
          <ArrowLeft className="w-4 h-4 mr-2" />{backLabel || "Retour"}
        </Button>
      )}
      <h2 className="text-2xl font-bold text-white mb-5 flex items-center gap-2">
        <Search className="w-6 h-6 text-red-500" />Explorer toutes les cartes
      </h2>

      <div className="flex flex-col sm:flex-row gap-3 mb-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <Input placeholder="Nom du Pokémon..." className="search-input pl-10"
            value={nameInput} onChange={(e) => setNameInput(e.target.value)} />
        </div>
        <div className="flex-1">
          <IllustratorPicker illustrators={filterOptions.illustrators}
            value={illustratorInput} onChange={setIllustratorInput} />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-6">
        {filterOptions.rarities.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant="outline" className={`border-zinc-700 text-white ${rarity ? "bg-zinc-800" : ""}`}>
                {rarity || "Rareté"}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-zinc-900 border-zinc-800 text-white max-h-72 overflow-y-auto">
              <DropdownMenuItem onClick={() => setRarity(null)} className="cursor-pointer focus:bg-zinc-800 focus:text-white">
                Toutes les raretés
              </DropdownMenuItem>
              {filterOptions.rarities.map(r => (
                <DropdownMenuItem key={r} onClick={() => setRarity(r)} className="cursor-pointer focus:bg-zinc-800 focus:text-white">
                  {r}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        {filterOptions.categories.map(cat => (
          <Button key={cat} size="sm" variant="outline"
            className={`border-zinc-700 text-white ${category === cat ? "bg-zinc-800" : ""}`}
            onClick={() => setCategory(category === cat ? null : cat)}>
            {cat}
          </Button>
        ))}
        <Button size="sm" variant="outline"
          title="TCG Pocket est un jeu à part : ses cartes peuvent être écartées des résultats"
          className={`border-zinc-700 text-white ${excludeTcgp ? "bg-zinc-800" : ""}`}
          onClick={() => setExcludeTcgp(v => !v)}>
          {excludeTcgp ? "TCG Pocket masqué" : "TCG Pocket inclus"}
        </Button>
        {activeFilterCount > 0 && (
          <Button size="sm" variant="ghost" className="text-zinc-400 hover:text-white" onClick={clearFilters}>
            <X className="w-4 h-4 mr-1" />Réinitialiser
          </Button>
        )}
        <div className="flex-1" />
        <Button size="sm" variant="outline"
          className={`border-zinc-700 text-white ${selectMode ? "bg-blue-500/20 border-blue-500 text-blue-400" : ""}`}
          onClick={toggleSelectMode}>
          <ListChecks className="w-4 h-4 mr-2" />
          {selectMode ? "Quitter la sélection" : "Mode sélection"}
        </Button>
      </div>

      {selectMode && (
        <div className="flex flex-wrap items-center gap-3 mb-5 p-3 bg-zinc-900/50 border border-zinc-800 rounded-xl">
          <span className="text-sm text-zinc-400">{selectedIds.size} sélectionnée{selectedIds.size > 1 ? "s" : ""}</span>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant="outline" className="border-zinc-700 text-white">
                <Palette className="w-4 h-4 mr-2" />Variant : {LABELS[bulkVariant] || bulkVariant}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-zinc-900 border-zinc-800 text-white">
              {VARIANT_ORDER.map(type => (
                <DropdownMenuItem key={type} onClick={() => setBulkVariant(type)} className="cursor-pointer focus:bg-zinc-800 focus:text-white">
                  {LABELS[type]}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <div className="w-full">
            <AddToListButtons lists={lists} disabled={selectedIds.size === 0}
              onAddToList={addSelectionToList} onCreateList={onCreateList} />
          </div>
        </div>
      )}

      {cards.length === 0 && !loading ? (
        <div className="text-center py-16 text-zinc-500">
          <Library className="w-12 h-12 mx-auto mb-4 opacity-50" /><p>Aucune carte trouvée.</p>
        </div>
      ) : (
        <div className="card-grid">
          {cards.map(card => (
            <PokemonCardTile
              key={card.id}
              card={card}
              collectionMap={collection}
              conditionMap={conditions}
              onCardClick={onCardClick}
              onVariantClick={handleVariantClick}
              cardVariants={computeVariants(card)}
              selectMode={selectMode}
              selected={selectedIds.has(card.id)}
              onToggleSelect={toggleCardSelection}
            />
          ))}
        </div>
      )}

      {loading && (
        <div className="flex items-center justify-center py-10">
          <Loader2 className="w-8 h-8 animate-spin text-red-500" />
        </div>
      )}

      {!loading && hasMore && (
        <div className="text-center mt-6">
          <Button variant="outline" className="border-zinc-700 text-white" onClick={() => setPage(p => p + 1)}>
            Charger plus de cartes
          </Button>
        </div>
      )}
    </div>
  );
};
